// services/authService.ts
import { apiService } from './api';
import {
  LoginRequest,
  LoginResponse,
  RefreshResponse,
  ResetPasswordRequest,
  User,
} from '../types/index';

class AuthService {
  // ── Login ──────────────────────────────────────────────────────────────────
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return apiService.post<LoginResponse>('/auth/login/', {
      email: credentials.username,
      password: credentials.password,
    });
  }

  // ── Logout (blacklist refresh token) ──────────────────────────────────────
  async logout(refreshToken: string): Promise<void> {
    return apiService.post('/auth/logout/', { refresh: refreshToken });
  }

  // ── Current user profile ──────────────────────────────────────────────────
  async getProfile(): Promise<User> {
    const response = await apiService.get<{ success: boolean; user: User }>('/auth/me/');
    return response.user;
  }

  // ── Refresh access token ──────────────────────────────────────────────────
  async refreshToken(refresh: string): Promise<RefreshResponse> {
    return apiService.post<RefreshResponse>('/auth/token/refresh/', { refresh });
  }

  // ── Forgot password (sends reset email) ───────────────────────────────────
  async forgotPassword(email: string): Promise<void> {
    return apiService.post('/auth/forgot-password/', { email });
  }

  // ── Validate reset token (frontend pre-check) ─────────────────────────────
  async validateResetToken(token: string): Promise<{ message: string; expires_at: string }> {
    return apiService.get(`/auth/reset-password/validate/?token=${token}`);
  }

  // ── Reset password with token ─────────────────────────────────────────────
  async resetPassword(payload: ResetPasswordRequest): Promise<void> {
    return apiService.post('/auth/reset-password/', payload);
  }

  // ── Change password (authenticated) ───────────────────────────────────────
  async changePassword(payload: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<void> {
    return apiService.post('/auth/change-password/', payload);
  }

  // ── OTP verification shim (maps to reset-password validate route) ─────────
  async verifyOTP(payload: { email: string; otp_code: string }): Promise<void> {
    // The backend uses token-based reset (no separate OTP endpoint).
    // Here the token IS the OTP / link token sent via email.
    return apiService.post('/auth/reset-password/validate/', { token: payload.otp_code });
  }
}

export const authService = new AuthService();
